import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Sun, Moon, UserPlus, Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import api from "../api/index";
import { useTheme } from "../context/ThemeContext";

function getPasswordStrength(password) {
  if (password.length === 0) return null;
  if (password.length < 6)
    return {
      level: 0,
      label: "Çok kısa — en az 6 karakter gerekli",
      color: "#c0392b",
    };
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;
  if (score <= 1) return { level: 1, label: "Zayıf", color: "#c0392b" };
  if (score <= 2) return { level: 2, label: "Orta", color: "#e67e22" };
  return { level: 3, label: "Güçlü", color: "#27ae60" };
}

export default function Register() {
  const { theme, isDark, toggleTheme } = useTheme();
  const [form, setForm] = useState({
    username: "",
    email: "",
    first_name: "",
    last_name: "",
    password: "",
  });
  const [showPass, setShowPass] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) =>
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));

  const strength = getPasswordStrength(form.password);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (form.password.length < 6) {
      setError("Şifre en az 6 karakter olmalıdır.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/auth/register", form);
      toast.success("Kayıt başarılı!");
      setTimeout(() => navigate("/login"), 1500);
    } catch (err) {
      setError(err.response?.data?.detail || "Kayıt başarısız.");
    } finally {
      setLoading(false);
    }
  };

  const t = theme;
  const inputStyle = {
    background: t.input,
    border: `1px solid ${t.inputBorder}`,
    borderRadius: 9,
    padding: "10px 13px",
    color: t.text,
    fontSize: 13,
    outline: "none",
    fontFamily: "inherit",
    width: "100%",
    boxSizing: "border-box",
  };
  const labelStyle = {
    fontSize: 11,
    fontWeight: 500,
    color: t.textSecondary,
    letterSpacing: 0.5,
    textTransform: "uppercase",
  };

  return (
    <div
      style={{
        minHeight: "100vh",
        width: "100%",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        background: t.bg,
        fontFamily: "'Inter',sans-serif",
        transition: "background 0.3s",
      }}
    >
      <div style={{ width: 400, position: "relative" }}>
        <div style={{ textAlign: "center", marginBottom: 32 }}>
          <div
            style={{
              fontFamily: "'Playfair Display',serif",
              fontSize: 28,
              fontWeight: 600,
              color: t.text,
              letterSpacing: 0.5,
            }}
          >
            TurnChat
          </div>
          <div
            style={{
              fontSize: 12,
              color: t.textMuted,
              marginTop: 5,
              letterSpacing: 0.3,
            }}
          >
            yeni hesap oluştur
          </div>
        </div>

        <div
          style={{
            background: t.surface,
            border: `1px solid ${t.border}`,
            borderRadius: 16,
            padding: "32px 28px",
            position: "relative",
          }}
        >
          <button
            onClick={toggleTheme}
            style={{
              position: "absolute",
              top: 14,
              right: 14,
              background: "transparent",
              border: `1px solid ${t.border}`,
              borderRadius: 9,
              color: t.textMuted,
              cursor: "pointer",
              width: 34,
              height: 34,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            {isDark ? <Sun size={14} /> : <Moon size={14} />}
          </button>
          <form
            onSubmit={handleSubmit}
            style={{ display: "flex", flexDirection: "column", gap: 16 }}
          >
            <div style={{ display: "flex", gap: 12 }}>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  flex: 1,
                }}
              >
                <label style={labelStyle}>Ad</label>
                <input
                  style={inputStyle}
                  name="first_name"
                  value={form.first_name}
                  onChange={handleChange}
                  placeholder="Burak"
                  required
                />
              </div>
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                  flex: 1,
                }}
              >
                <label style={labelStyle}>Soyad</label>
                <input
                  style={inputStyle}
                  name="last_name"
                  value={form.last_name}
                  onChange={handleChange}
                  placeholder="Kaya"
                  required
                />
              </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={labelStyle}>Kullanıcı Adı</label>
              <input
                style={inputStyle}
                name="username"
                value={form.username}
                onChange={handleChange}
                placeholder="kullanici_adi"
                required
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={labelStyle}>E-posta</label>
              <input
                style={inputStyle}
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="ornek@mail.com"
                required
              />
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <label style={labelStyle}>Şifre</label>
              <div style={{ position: "relative" }}>
                <input
                  style={{ ...inputStyle, padding: "10px 38px 10px 13px" }}
                  type={showPass ? "text" : "password"}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="••••••"
                  required
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  style={{
                    position: "absolute",
                    right: 10,
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "transparent",
                    border: "none",
                    color: t.textMuted,
                    cursor: "pointer",
                    display: "flex",
                    alignItems: "center",
                  }}
                >
                  {showPass ? <EyeOff size={14} /> : <Eye size={14} />}
                </button>
              </div>

              {form.password.length > 0 && (
                <div style={{ marginTop: 4 }}>
                  <div style={{ display: "flex", gap: 4, marginBottom: 5 }}>
                    {[1, 2, 3].map((i) => (
                      <div
                        key={i}
                        style={{
                          flex: 1,
                          height: 3,
                          borderRadius: 2,
                          background:
                            strength && strength.level >= i
                              ? strength.color
                              : t.border,
                          transition: "background 0.2s",
                        }}
                      />
                    ))}
                  </div>
                  <div
                    style={{
                      fontSize: 11,
                      color: strength ? strength.color : t.textMuted,
                    }}
                  >
                    {strength?.label}
                  </div>
                </div>
              )}
            </div>

            {error && (
              <div
                style={{
                  background: t.error,
                  border: `1px solid ${t.errorBorder}`,
                  borderRadius: 8,
                  padding: "9px 12px",
                  color: t.errorText,
                  fontSize: 12,
                }}
              >
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading || (strength !== null && strength.level === 0)}
              style={{
                background: t.accent,
                border: "none",
                borderRadius: 9,
                padding: "11px",
                color: t.accentText,
                fontSize: 13,
                fontWeight: 600,
                cursor:
                  loading || (strength !== null && strength.level === 0)
                    ? "not-allowed"
                    : "pointer",
                fontFamily: "inherit",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 7,
                opacity:
                  loading || (strength !== null && strength.level === 0)
                    ? 0.5
                    : 1,
                transition: "opacity 0.2s",
                marginTop: 2,
              }}
            >
              <UserPlus size={14} />
              {loading ? "Kayıt yapılıyor..." : "Kayıt Ol"}
            </button>
          </form>

          <div
            style={{
              borderTop: `1px solid ${t.border}`,
              marginTop: 22,
              paddingTop: 18,
              textAlign: "center",
            }}
          >
            <span style={{ fontSize: 12, color: t.textMuted }}>
              Zaten hesabın var mı?{" "}
            </span>
            <Link
              to="/login"
              style={{
                fontSize: 12,
                color: t.accent,
                textDecoration: "none",
                fontWeight: 500,
              }}
            >
              Giriş yap
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
